import { PurchaseMethods } from '@sm360/widget-commons-vehicle';
import { filter, map, flatten, isArray, sortBy, isEmpty, max } from 'lodash';
import { LocaleService } from '@sm360/widget-commons-core-services';
import VehicleCriteriaModel from 'inventory-listing/models/VehicleCriteriaModel.js';

const { PaymentFrequencies, PaymentFrequencyService } = PurchaseMethods;

export function translateListingUsed(listingUsed) {
    return listingUsed.setIn(
        ['paymentOptions', 'frequencyValues'],
        PaymentFrequencyService.getPaymentFrequencyDropDownValues(LocaleService)
    );
}

export function setPreferredPaymentFrequency(listingUsed, paymentFrequency) {
    const paymentRangeMinEnabled = listingUsed.paymentOptions.range[0] > listingUsed.paymentOptions.rangeBoundaries[0];
    const paymentRangeMaxEnabled = listingUsed.paymentOptions.range[1] < listingUsed.paymentOptions.rangeBoundaries[1];

    let paymentRangeBoundaries;
    let paymentRangeStep;
    switch (paymentFrequency) {
        case PaymentFrequencies.WEEKLY:
            paymentRangeBoundaries = [0, 500];
            paymentRangeStep = 25;
            break;

        case PaymentFrequencies.BI_WEEKLY:
            paymentRangeBoundaries = [0, 1000];
            paymentRangeStep = 50;
            break;

        case PaymentFrequencies.SEMI_MONTHLY:
            paymentRangeBoundaries = [0, 1000];
            paymentRangeStep = 50;
            break;

        case PaymentFrequencies.MONTHLY:
            paymentRangeBoundaries = [0, 2000];
            paymentRangeStep = 100;
            break;

        default:
            paymentRangeBoundaries = [0, 500];
            paymentRangeStep = 25;
            break;
    }

    let paymentRange = listingUsed.paymentOptions.range;
    if (paymentRangeMinEnabled || paymentRangeMaxEnabled) {
        const paymentRangeMin = Math.floor(
            (paymentRange[0] * listingUsed.paymentOptions.preferredPaymentFrequency) / paymentFrequency
        );
        const paymentRangeMax = Math.ceil(
            (paymentRange[1] * listingUsed.paymentOptions.preferredPaymentFrequency) / paymentFrequency
        );

        if (paymentRangeMinEnabled) {
            paymentRange[0] = paymentRangeMin + (paymentRangeMin % 10 > 0 ? (paymentRangeMin % 10) - 10 : 0);
        }
        if (paymentRangeMaxEnabled) {
            paymentRange[1] = paymentRangeMax + (paymentRangeMax % 10 > 0 ? 10 - (paymentRangeMax % 10) : 0);
        }
    } else {
        paymentRange = paymentRangeBoundaries;
    }

    return listingUsed
        .setIn(['paymentOptions', 'preferredPaymentFrequency'], paymentFrequency)
        .setIn(['paymentOptions', 'rangeEnabled'], paymentRangeMinEnabled || paymentRangeMaxEnabled)
        .setIn(['paymentOptions', 'rangeStep'], paymentRangeStep)
        .setIn(['paymentOptions', 'rangeBoundaries'], paymentRangeBoundaries)
        .setIn(['paymentOptions', 'range'], paymentRange);
}

export function addVehicleCriteria(listingUsed, criteria) {
    let currentVehicleCriteria = listingUsed.vehicleCriteria;
    let newCriteria;

    if (isArray(criteria.metadatas)) {
        const filteredMetadatas = filter(criteria.metadatas, (metadata) => {
            return metadata.isSelected;
        });
        newCriteria = flatten(
            map(filteredMetadatas, (metadata) => {
                return metadata.code;
            })
        );
    } else {
        newCriteria = criteria.metadatas;
    }

    currentVehicleCriteria = currentVehicleCriteria.set(criteria.attribute, newCriteria);

    // makeIds and modelIds replace queryString catalog ids
    if (criteria.attribute === 'makeIds' || criteria.attribute === 'modelIds') {
        currentVehicleCriteria = currentVehicleCriteria.set('catalogMakeIds', []);
        currentVehicleCriteria = currentVehicleCriteria.set('catalogModelIds', []);
    }

    return listingUsed.set('vehicleCriteria', currentVehicleCriteria);
}

export function resetVehicleCriteria(listingUsed) {
    const currentVehicleCriteria = listingUsed.vehicleCriteria;
    const defaultVehicleCriteria = currentVehicleCriteria.mapToLimitedMandatoryPropertySetObject();
    return listingUsed.set('vehicleCriteria', new VehicleCriteriaModel(defaultVehicleCriteria));
}

/**
 *
 * @param [listingUsed] State listing used data
 * @param [paymentFrequencies] payment frequencies - [12, 26,52]
 * @param [term] payment terms - [24, 36, 48, 60, 72, 84]
 */
export function updateAvailablePaymentOptions(listingUsed, paymentFrequencies, terms, defaultTerm) {
    const selectedTerm = listingUsed.paymentOptions.maximumTerm;
    const selectedPaymentFrequency = listingUsed.paymentOptions.preferredPaymentFrequency;
    let updatedListingUsed = listingUsed;

    // If the preferred term is not part of the available terms, select the maximum value among those available
    if (!isEmpty(terms) && !terms.find((f) => f === selectedTerm)) {
        updatedListingUsed = updatedListingUsed.setIn(['paymentOptions', 'maximumTerm'], max(terms) || 0);
    }
    // If the preferred paymentFrequency is not part of the available frequencies, select the maximum value among those available
    if (!paymentFrequencies.find((f) => f === selectedPaymentFrequency)) {
        updatedListingUsed = updatedListingUsed.setIn(
            ['paymentOptions', 'preferredPaymentFrequency'],
            max(paymentFrequencies) || 0
        );
    }
    return updatedListingUsed
        .setIn(
            ['paymentOptions', 'frequencyValues'],
            PaymentFrequencyService.getPaymentFrequencyDropDownValuesV25(LocaleService, paymentFrequencies)
        )
        .setIn(['paymentOptions', 'terms'], terms)
        .setIn(['paymentOptions', 'defaultTerm'], defaultTerm);
}

export function getFilters(filters, filterName) {
    if (!filterName || !filters.length) {
        return [];
    }

    const metadataCategory = filters.find((metadata) => {
        return metadata.name === filterName;
    });

    if (!metadataCategory) {
        return [];
    }

    // Move the undefined metadata (code = -1) to the last index
    // then sort by label
    return sortBy(metadataCategory.metadatas, [({ code }) => code === -1, 'label'], ['asc', 'asc']);
}

/*
 * @param [plainFieldName] full name of the sorting field (ex: SALE_PRICE_ASC)
 * @returns an object with the following shape:
    {
        direction: 'ASC',
        vehicleSortParameter: 'SALE_PRICE',
    }
 */
export function getSortObjectFromField(plainFieldName) {
    if (!plainFieldName.includes('_'))
        return {
            direction: 'ASC',
            vehicleSortParameter: 'SALE_PRICE',
        };

    let sort = plainFieldName.split('_');
    const direction = sort && sort.pop();
    if (sort?.[0] === 'PRICE') sort = ['SALE_PRICE'];
    const vehicleSortParameter = sort && sort.join('_');
    return {
        direction,
        vehicleSortParameter,
    };
}
